import {ContentBox} from '@hconnect/common/components'
import {getHighlightedTitle} from '@hconnect/common/utils'
import {useUpmPlant} from '@hconnect/upm'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useHasPermission} from '@hproduce/permissions'
import {DeleteOutlined, EditOutlined, MoreVert} from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {Typography, IconButton, Stack, Menu, MenuItem} from '@mui/material'
import {ConfirmationDialog, EventEditDialog} from '@widgets/shift-event-details'
import {useState, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useDeactivateProcessWorkOrderSeries, useDeleteProcessWorkOrder} from '../../hooks'
import {
  HProduceTask,
  ProcessWorkOrderRepetitionInfoDto,
  ScheduleState,
  SideCardItemMode
} from '../../types'

import {MetaAnnotation} from './MetaAnnotation'

export type ProcessWorkOrderTaskCardProps = {
  item: Omit<HProduceTask, 'assignees'> & {
    lastUpdatedAt?: string
    repetitionInfo?: ProcessWorkOrderRepetitionInfoDto
    scheduleId?: string
  }
  onClose: (original?: HProduceTask, editVersion?: HProduceTask) => void
  onEditTaskClick: (taskId: string, mode: SideCardItemMode) => void
  freeSearchText?: string
  timezone: string
  children?: React.ReactNode
  setIsLoadingEditingDeleting: (isLoading: boolean) => void
}

export type ProcessWorkOrderTaskCardItem = ProcessWorkOrderTaskCardProps['item']

const getTaskTitle = (title: string, freeSearchText?: string) =>
  freeSearchText ? getHighlightedTitle(title, freeSearchText) : title

export const ProcessWorkOrderTaskCard: React.FC<ProcessWorkOrderTaskCardProps> = (props) => {
  const {
    item,
    onClose,
    onEditTaskClick,
    freeSearchText,
    timezone,
    setIsLoadingEditingDeleting,
    children
  } = props
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const menuButtonRef = useRef(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)
  const upmPlant = useUpmPlant({plantId: item.plantId, shouldRaiseError: false})
  const hasPermission = useHasPermission({
    plantId: item.plantId,
    countryId: upmPlant?.country ?? ''
  })
  const canEditProcessWorkOrder = hasPermission('EDIT_PROCESS_WORK_ORDER')
  const canDeleteProcessWorkOrder = hasPermission('DELETE_PROCESS_WORK_ORDER')
  const hasMenuActions = canEditProcessWorkOrder || canDeleteProcessWorkOrder
  const deleteProcessWorkOrder = useDeleteProcessWorkOrder()
  const deactivateProcessWorkOrderSeries = useDeactivateProcessWorkOrderSeries()

  const closeMenu = () => {
    setAnchorEl(null)
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const openDialog = () => {
    setAnchorEl(null)
    setShowDeleteDialog(true)
  }

  const handleCancel = () => {
    setShowDeleteDialog(false)
    setShowEditDialog(false)
  }

  const handleDelete = async () => {
    try {
      setIsLoadingEditingDeleting(true)

      await deleteProcessWorkOrder.mutateAsync({
        plantId: item.plantId,
        externalId: item.externalId
      })
    } catch (err) {
      spawnErrorToast(t('processWorkOrders.message.failedToDeleteProcessWorkOrder'))
    } finally {
      onClose()
      setTimeout(() => setIsLoadingEditingDeleting(false), 100)
      spawnSuccessToast(t('processWorkOrders.message.processWorkOrderDeleted'))
    }
  }

  const handleDeleteSeries = () => {
    deactivateProcessWorkOrderSeries.mutate({
      plantId: item.plantId,
      scheduleId: item.scheduleId as string,
      state: ScheduleState.INACTIVE.toString()
    })
    onClose()
  }

  const handleEdit = () => {
    if (item?.repetitionInfo?.repetition) {
      setShowEditDialog(true)
    } else {
      onEditTaskClick(item.id, SideCardItemMode.EditSingle)
    }
  }

  return (
    <ContentBox
      data-test-id="task-details"
      mode="max100PercentOfParentHeight"
      bodyWithPadding
      title={
        <Typography
          component="span"
          variant="h2"
          sx={{textTransform: 'none'}}
          data-test-id="task-details-title"
          display="flex"
          alignItems="center"
        >
          {getTaskTitle(item.title, freeSearchText)}
        </Typography>
      }
      afterTitle={
        <>
          {hasMenuActions && (
            <>
              <IconButton
                sx={{p: 1}}
                ref={menuButtonRef}
                onClick={openMenu}
                color="primary"
                data-test-id="task-details-more-button"
              >
                <MoreVert color="primary" />
              </IconButton>
              <Menu
                data-test-id="task-details-more-menu"
                anchorEl={menuButtonRef.current}
                open={isMenuOpen}
                onClose={closeMenu}
              >
                {canDeleteProcessWorkOrder && (
                  <MenuItem onClick={openDialog} data-test-id="task-details-delete-button">
                    <Typography variant="subtitle1">
                      <DeleteOutlined sx={{mr: 2, verticalAlign: 'text-bottom'}} />
                      {t('task.action.delete')}
                    </Typography>
                  </MenuItem>
                )}
                {canEditProcessWorkOrder && (
                  <MenuItem onClick={handleEdit} data-test-id="task-details-edit-button">
                    <Typography variant="subtitle1">
                      <EditOutlined sx={{mr: 2, verticalAlign: 'text-bottom'}} />
                      {t('task.action.edit')}
                    </Typography>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
          <IconButton onClick={() => onClose()} data-test-id="task-details-close">
            <CloseIcon color="primary" />
          </IconButton>
        </>
      }
      headerSx={{paddingBottom: 0}}
    >
      <Stack spacing={2} useFlexGap>
        <Stack>
          <MetaAnnotation
            label={t('metaAnnotation.label.created')}
            date={item.created}
            timezone={timezone}
            data-test-id="meta-annotation-created-on"
          />
          {item.lastUpdatedAt && (
            <MetaAnnotation
              label={t('metaAnnotation.label.lastUpdated')}
              date={item.lastUpdatedAt}
              timezone={timezone}
              data-test-id="meta-annotation-last-updated"
            />
          )}
        </Stack>
        {children}
      </Stack>
      {showDeleteDialog && (
        <ConfirmationDialog
          onDeleteSingle={handleDelete}
          onDeleteSeries={handleDeleteSeries}
          onClose={handleCancel}
          title={item.title ?? ''}
          isSeriesItem={!!item.repetitionInfo?.repetition}
          additionalMessage={
            item.repetitionInfo?.repetition
              ? t('task.message.additionalDeleteRecurringPWODialogText')
              : ''
          }
        />
      )}
      {showEditDialog && (
        <EventEditDialog
          setEditMode={(mode) =>
            onEditTaskClick(
              item.id,
              mode === 'series' ? SideCardItemMode.EditSeries : SideCardItemMode.EditSingle
            )
          }
          isRepeating={!!item.repetitionInfo?.repetition}
          doClose={handleCancel}
        />
      )}
    </ContentBox>
  )
}
